"use client";

import { ArrowRight, Search, ShieldCheck, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
export default function Sourcingexpertise() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const steps = [{
    number: "01",
    title: "Global Medical Sourcing",
    description: "We identify innovative medical technologies from trusted international manufacturers, helping partners discover products with clinical value and global market potential.",
    Icon: Search
  }, {
    number: "02",
    title: "Clinical & Market Evaluation",
    description: "We provide preliminary evaluations of clinical applications, product positioning, and market opportunities to support informed business decisions.",
    Icon: ShieldCheck
  }, {
    number: "03",
    title: "Strategic Business Partnerships",
    description: "We facilitate collaborations between manufacturers, distributors, and healthcare organizations, creating opportunities for sustainable international growth.",
    Icon: Globe
  }];
  const collagePhotos = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/sourcing_global_network.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/sourcing_global_network.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/sourcing_global_network.png-wvc-sizes",
    alt: "Global medical supply chain network visualization",
    rotation: "-rotate-4",
    translate: "md:translate-x-0 md:translate-y-4",
    z: "z-10"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/sourcing_clinical_review.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/sourcing_clinical_review.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/sourcing_clinical_review.png-wvc-sizes",
    alt: "Medical professionals reviewing clinical device data",
    rotation: "rotate-0",
    translate: "md:-translate-y-6",
    z: "z-20"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/sourcing_logistics_support.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/sourcing_logistics_support.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/sourcing_logistics_support.webp-wvc-sizes",
    alt: "International medical device distribution logistics",
    rotation: "rotate-3",
    translate: "md:translate-x-2 md:translate-y-10",
    z: "z-10"
  }];
  return <section data-section-id="1493" id="sourcingexpertise" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32">
      <svg aria-hidden="true" className="pointer-events-none absolute -top-12 -right-10 h-72 w-72 opacity-60 md:h-96 md:w-96" viewBox="0 0 200 200" fill="none" stroke="var(--chart-3)" strokeWidth="1.5" strokeLinecap="round">
        <path d="M180 20 C140 60, 110 100, 80 160" />
        <path d="M180 20 C160 40, 145 45, 130 48" />
        <path d="M170 35 C150 55, 135 62, 118 68" />
        <path d="M155 55 C135 75, 120 82, 100 90" />
        <path d="M140 80 C120 100, 105 108, 88 116" />
        <path d="M125 105 C108 125, 95 135, 82 142" />
      </svg>

      <svg aria-hidden="true" className="pointer-events-none absolute -bottom-10 -left-10 h-64 w-64 rotate-180 opacity-60 md:h-80 md:w-80" viewBox="0 0 200 200" fill="none" stroke="var(--chart-3)" strokeWidth="1.5" strokeLinecap="round">
        <path d="M180 20 C140 60, 110 100, 80 160" />
        <path d="M180 20 C160 40, 145 45, 130 48" />
        <path d="M170 35 C150 55, 135 62, 118 68" />
        <path d="M155 55 C135 75, 120 82, 100 90" />
        <path d="M140 80 C120 100, 105 108, 88 116" />
      </svg>

      <div ref={ref} className={`relative mx-auto max-w-[1320px] px-6 transition-all duration-300 ease-out md:px-8 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="font-default text-xs font-medium tracking-wide uppercase text-primary">
              Strategic Sourcing
            </div>

            <h2 className="font-default font-bold tracking-tight mt-6 text-5xl leading-[var(--line-height-heading)] text-foreground"><span className="font-default italic font-medium" style={{
              color: 'var(--primary)'
            }}>Connecting Global Innovation to Healthcare&nbsp;</span></h2>

            <p className="font-default mt-6 max-w-md text-base leading-[var(--line-height-normal)] text-secondary-foreground !font-normal"><span className="!font-medium">MS Biotechnology </span>connects global medical innovation with healthcare markets through strategic sourcing, clinical evaluation, and international business development. We identify high-potential medical technologies by combining clinical insight, market assessment, and preliminary regulatory feasibility review, helping our partners make informed business decisions with confidence.</p>

            <ul className="mt-12 flex flex-col">
              {steps.map((step, i) => {
              const StepIcon = step.Icon;
              return <li key={step.number} data-index={i} className="group relative py-7">
                    {i !== 0 && <div aria-hidden="true" className="absolute top-0 right-0 left-0 flex items-center">
                        <span className="h-1.5 w-1.5 rounded-lg" style={{
                    backgroundColor: "var(--primary)"
                  }} />
                        <span className="h-px flex-1" style={{
                    backgroundImage: "repeating-linear-gradient(to right, var(--foreground) 0 6px, transparent 6px 11px)",
                    opacity: 0.35
                  }} />
                        <span className="h-1.5 w-1.5 rounded-lg" style={{
                    backgroundColor: "var(--primary)"
                  }} />
                      </div>}

                    <div className="flex items-start gap-5">
                      <div className="relative flex-shrink-0">
                        <span aria-hidden="true" className="absolute inset-0 m-auto h-10 w-10 rounded-lg" style={{
                      backgroundColor: "var(--primary)",
                      opacity: 0.15
                    }} />
                        <div className="relative flex h-10 w-10 items-center justify-center">
                          <StepIcon className="h-5 w-5" strokeWidth={1.75} style={{
                        color: "var(--primary)"
                      }} />
                        </div>
                      </div>

                      <div className="flex-1">
                        <div className="flex items-baseline gap-3">
                          <span className="font-default text-xs font-medium tracking-[0.16em] text-primary/80">
                            {step.number}
                          </span>
                          <h3 className="font-default text-xl font-semibold tracking-tight text-foreground">
                            {step.title}
                          </h3>
                        </div>
                        <p className="font-default mt-2 text-base leading-[var(--line-height-normal)] text-muted-foreground">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </li>;
            })}

              <li aria-hidden="true" className="relative">
                <div className="flex items-center">
                  <span className="h-1.5 w-1.5 rounded-lg" style={{
                  backgroundColor: "var(--primary)"
                }} />
                  <span className="h-px flex-1" style={{
                  backgroundImage: "repeating-linear-gradient(to right, var(--foreground) 0 6px, transparent 6px 11px)",
                  opacity: 0.35
                }} />
                  <span className="h-1.5 w-1.5 rounded-lg" style={{
                  backgroundColor: "var(--primary)"
                }} />
                </div>
              </li>
            </ul>

            <div className="mt-10">
              <Link to="#process">
                
              </Link>
            </div>
          </div>

          <div className="relative lg:col-span-7">
            <div aria-hidden="true" className="pointer-events-none absolute top-1/2 left-1/2 h-[280px] w-[280px] -translate-x-1/2 -translate-y-1/2 rounded-lg md:h-[420px] md:w-[420px]" style={{
            backgroundColor: "var(--primary)",
            opacity: 0.1
          }} />

            <div className="relative hidden h-full min-h-[560px] items-center justify-center md:flex">
              <div className="relative grid w-full grid-cols-3 gap-5">
                {collagePhotos.map((photo, i) => <div key={photo.src} data-index={i} className={`relative ${photo.z} ${photo.rotation} ${photo.translate} transition-transform duration-300 ease-out hover:rotate-0 hover:scale-[1.03]`}>
                    <div className="overflow-hidden rounded-lg shadow-md" style={{
                  aspectRatio: "4 / 5"
                }}>
                      <img src={photo.src} data-wvc-srcset={photo.srcset} data-wvc-sizes={photo.sizes} alt={photo.alt} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="md:hidden">
              <div className="relative -mx-6 overflow-x-auto px-6 pb-4">
                <div className="flex gap-4">
                  {collagePhotos.map((photo, i) => <div key={photo.src} data-index={i} className="relative flex-shrink-0" style={{
                  width: "70%"
                }}>
                      <div className="overflow-hidden rounded-lg shadow-md" style={{
                    aspectRatio: "4 / 5"
                  }}>
                        <img src={photo.src} data-wvc-srcset={photo.srcset} data-wvc-sizes={photo.sizes} alt={photo.alt} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}